export type ListViewColumnKey =
    | 'title'
    | 'dueDate'
    | 'priority'
    | 'status'
    | 'users'
    | 'project'
    | 'taskType'
    | 'createdAt'
    | 'creator'
    | 'timeTracking'
    | 'totalTimeSpent'

export type ListViewColumnState = Record<ListViewColumnKey, boolean>

const PROJECT_LIST_VIEW_COLUMNS_STORAGE_PREFIX = 'project-list-view-columns'
const PROJECT_LIST_VIEW_COLUMNS_VERSION = 4

export const LIST_VIEW_COLUMN_ORDER: ListViewColumnKey[] = [
    'title',
    'dueDate',
    'priority',
    'status',
    'users',
    'project',
    'taskType',
    'createdAt',
    'creator',
    'timeTracking',
    'totalTimeSpent',
]

export function getDefaultListViewColumnState(): ListViewColumnState {
    return {
        title: true,
        dueDate: true,
        priority: true,
        status: true,
        users: true,
        project: false,
        taskType: false,
        createdAt: false,
        creator: false,
        timeTracking: true,
        totalTimeSpent: false,
    }
}

function getProjectListViewColumnsStorageKey(projectId: string | number) {
    return `${PROJECT_LIST_VIEW_COLUMNS_STORAGE_PREFIX}:${projectId}`
}

export function readProjectListViewColumns(projectId?: string | number | null) {
    const defaultState = getDefaultListViewColumnState()

    if (projectId == null) {
        return defaultState
    }

    try {
        const rawValue = localStorage.getItem(getProjectListViewColumnsStorageKey(projectId))

        if (!rawValue) {
            return defaultState
        }

        const parsedValue = JSON.parse(rawValue)

        if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
            return defaultState
        }

        const isLegacyState = parsedValue.__version !== PROJECT_LIST_VIEW_COLUMNS_VERSION

        return LIST_VIEW_COLUMN_ORDER.reduce((accum, key) => {
            accum[key] = key === 'title'
                ? true
                : key === 'timeTracking' && isLegacyState
                    ? defaultState[key]
                : typeof parsedValue[key] === 'boolean'
                    ? parsedValue[key]
                    : defaultState[key]
            return accum
        }, { ...defaultState })
    } catch (error) {
        return defaultState
    }
}

export function writeProjectListViewColumns(projectId: string | number, columns: ListViewColumnState) {
    try {
        localStorage.setItem(
            getProjectListViewColumnsStorageKey(projectId),
            JSON.stringify({
                ...columns,
                __version: PROJECT_LIST_VIEW_COLUMNS_VERSION,
            })
        )
    } catch (error) {
        console.error('Failed to persist list view columns:', error)
    }
}
